import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/features/auth';
import { useLayout } from '@/contexts/LayoutContext';
import { usePlugins } from '@/contexts/PluginContext';
import { UserMenu } from '../UserMenu';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { AImryAssistant } from '@/features/support';
import { DarkModeToggle } from '../DarkModeToggle';
import { TodayCalendarWidget } from '../TodayCalendarWidget';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { useTranslation } from 'react-i18next';
import { initArcadeSounds, getArcadeSounds } from '@/shared/utils/ArcadeSounds';
import useMediaQuery, { MOBILE_BREAKPOINT } from '@/shared/hooks/useMediaQuery';
import BottomNavVideogame from './BottomNavVideogame';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BookmarkCheck,
  CalendarDays,
  DoorOpen,
  Gamepad2,
  Files,
  Wrench,
} from 'lucide-react';

/**
 * SidebarLayoutVideogame Component
 * VideoGame/Arcade themed sidebar navigation layout
 *
 * Design Philosophy:
 * - Arcade-style sidebar with thick borders and pixel fonts
 * - Sticky header with utilities only (notifications, lang, user)
 * - Navigation items in sidebar with arcade button styling
 */
export default function SidebarLayoutVideogame() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useLayout();
  const { isPluginEnabled, loading: pluginsLoading } = usePlugins();
  const { settings } = useSystemSettings();
  const [rainbowMode, setRainbowMode] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // Responsive: detect mobile viewport (<700px)
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const isActive = (path: string) => {
    const [pathPart, queryPart] = path.split('?');
    if (queryPart) {
      // For paths with query params (e.g. ?book=room), match both pathname and query param
      return location.pathname.startsWith(pathPart) && location.search.includes(queryPart);
    }
    // For plain paths, match pathname but NOT if a query-param sibling is active
    return location.pathname.startsWith(pathPart) && !location.search.includes('book=');
  };

  // Initialize 8-bit sound effects on first user interaction
  useEffect(() => {
    const initSound = () => {
      initArcadeSounds();
      document.removeEventListener('click', initSound);
    };
    document.addEventListener('click', initSound);

    return () => {
      document.removeEventListener('click', initSound);
    };
  }, []);

  // Scroll detection for header shadow effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setHeaderScrolled(true);
      } else {
        setHeaderScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Konami Code Easter Egg: ↑↑↓↓←→←→BA
  useEffect(() => {
    let konamiCode: string[] = [];
    const konamiPattern = [
      'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
      'b', 'a'
    ];

    const handleKeyDown = (e: KeyboardEvent) => {
      konamiCode.push(e.key);
      konamiCode = konamiCode.slice(-10);

      if (JSON.stringify(konamiCode) === JSON.stringify(konamiPattern)) {
        // ACTIVATE RAINBOW MODE!
        setRainbowMode(true);
        getArcadeSounds()?.playLevelUp();

        // Show points popup
        const points = document.createElement('div');
        points.className = 'points-popup';
        points.textContent = '+1000 🏆';
        points.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          font-family: var(--font-pixel), monospace;
          color: #39FF14;
          font-size: 48px;
          text-shadow: 0 0 20px #39FF14;
          animation: pointsPopup 2s ease-out forwards;
          pointer-events: none;
        `;
        document.body.appendChild(points);
        setTimeout(() => points.remove(), 2000);

        // Turn off rainbow mode after 10 seconds
        setTimeout(() => setRainbowMode(false), 10000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Icon mapping for navigation items
  const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
    '/bookings/my-bookings': BookmarkCheck,
    '/calendar': CalendarDays,
    '/bookings/my-bookings?book=room': DoorOpen,
    '/bookings/my-bookings?book=equipment': Wrench,
    '/files': Files,
  };

  // Unified Neo-Brutalism: White default, Pink active (all buttons same)
  // All navigation buttons use consistent styling

  // Build navigation items (all wait for plugin loading to avoid staggered render)
  const navItems = useMemo(() => {
    if (pluginsLoading) return [];

    const items: Array<{ path: string; label: string }> = [];

    // Booking plugin nav items
    if (isPluginEnabled('BOOKING')) {
      items.push(
        { path: '/bookings/my-bookings', label: t('navigation.myBookings') },
        { path: '/calendar', label: t('navigation.calendar') },
        { path: '/bookings/my-bookings?book=room', label: t('navigation.bookRoom') },
        { path: '/bookings/my-bookings?book=equipment', label: t('navigation.bookEquipment') },
      );
    }

    // Files (always visible once plugins have loaded)
    items.push({ path: '/files', label: t('navigation.files') });

    return items;
  }, [pluginsLoading, isPluginEnabled, t]);

  return (
    <>
      <div
        className={`game-container min-h-screen ${rainbowMode ? 'rainbow-mode' : ''}`}
        style={{
          backgroundColor: 'var(--theme-bg)',
          color: 'var(--theme-text)'
        }}
      >
        {/* Fixed Sidebar - Hidden on mobile, visible on desktop */}
        {!isMobile && (
        <aside
          className={`
            ${sidebarCollapsed ? 'w-20' : 'w-64'}
            fixed top-0 left-0 bottom-0 z-40
            flex flex-col
            transition-all duration-300 ease-in-out
            overflow-y-auto
          `}
        >
          {/* Logo/Brand - Clean Style */}
          <div className="h-24 flex items-center justify-center px-4">
            {settings?.navbarLogoData ? (
              <img
                src={settings.navbarLogoData}
                alt={settings.appName || 'Logo'}
                className={`transition-all duration-300 object-contain ${
                  sidebarCollapsed ? 'h-12 w-12' : (
                    settings.navbarLogoSize === 'sm' ? 'h-14 w-auto max-w-[200px]' :
                    settings.navbarLogoSize === 'lg' ? 'h-20 w-auto max-w-[200px]' :
                    'h-16 w-auto max-w-[200px]'  // md default
                  )
                }`}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-8 w-8" style={{ color: 'var(--theme-text)' }} />
                {!sidebarCollapsed && (
                  <span
                    className="text-lg font-bold uppercase"
                    style={{
                      color: 'var(--theme-text)',
                    }}
                  >
                    {settings?.appName || 'CHAMAMA'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Navigation Links - Neobrutalism Buttons (Theme-aware) */}
          <nav className="typo-navigation flex-1 px-3 py-4 space-y-3 overflow-y-auto">
            {pluginsLoading ? (
              // Skeleton placeholders while plugins load
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="neo-btn neo-btn-rounded flex items-center gap-2"
                  style={{
                    backgroundColor: 'var(--theme-surface)',
                    borderWidth: '4px',
                    borderStyle: 'solid',
                    borderColor: 'var(--theme-border)',
                    opacity: 0.5,
                    padding: sidebarCollapsed ? '0.5rem' : '0.5rem 0.75rem',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  }}
                >
                  <div style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: 'var(--theme-border)', opacity: 0.4 }} />
                  {!sidebarCollapsed && (
                    <div style={{ height: 10, borderRadius: 3, backgroundColor: 'var(--theme-border)', opacity: 0.4, width: `${60 + (i % 3) * 20}px` }} />
                  )}
                </div>
              ))
            ) : (
              navItems.map((item) => {
                const Icon = iconMap[item.path];
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`typo-navigation nav-button neo-btn neo-btn-rounded flex items-center gap-2 ${
                      sidebarCollapsed ? 'justify-center' : 'justify-start'
                    } ${active ? 'active' : ''}`}
                    style={{
                      backgroundColor: active ? 'var(--theme-accent)' : 'var(--theme-surface)',
                      color: 'var(--theme-text)',
                      textDecoration: 'none',
                      borderWidth: active ? '6px' : '4px',
                      borderStyle: 'solid',
                      borderColor: 'var(--theme-border)',
                    }}
                    title={sidebarCollapsed ? item.label : undefined}
                    onMouseEnter={() => getArcadeSounds()?.playHover()}
                    onClick={() => getArcadeSounds()?.playClick()}
                  >
                    {Icon && <Icon size={sidebarCollapsed ? 22 : 20} className="flex-shrink-0" />}
                    {!sidebarCollapsed && (
                      <span className="button-text" style={{ fontFamily: 'var(--font-pixel), monospace', fontSize: '14px', fontWeight: 900, WebkitTextStroke: '0.5px currentColor', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })
            )}
          </nav>

          {/* Today's Calendar Widget - Only show when sidebar is expanded */}
          {!sidebarCollapsed && <TodayCalendarWidget />}

          {/* Collapse/Expand Button - Theme-aware */}
          <button
            onClick={toggleSidebar}
            className="h-12 flex items-center justify-center transition-colors sidebar-collapse-btn"
            style={{
              backgroundColor: 'var(--theme-surface)',
              border: '4px solid var(--theme-border)',
              borderLeft: 'none',
              borderRight: 'none',
              boxShadow: 'none',
              color: 'var(--theme-text)'
            }}
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
        </aside>
        )}

        {/* Main Content Area with margin for fixed sidebar (no margin on mobile) */}
        <div
          className="flex-1 flex flex-col"
          style={{
            marginLeft: isMobile ? 0 : (sidebarCollapsed ? '5rem' : '16rem'),
            transition: 'margin-left 0.3s ease-in-out'
          }}
        >
          {/* Fixed Header - Theme-aware with scroll shadow (full width on mobile) */}
          <header
            className="fixed top-0 right-0 z-30 flex-shrink-0"
            style={{
              boxShadow: headerScrolled
                ? 'var(--theme-shadow)'
                : undefined,
              left: isMobile ? 0 : (sidebarCollapsed ? '5rem' : '16rem'),
              transition: 'left 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
            }}
          >

            <div className={`h-20 mx-auto ${isMobile ? 'px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'}`} dir="ltr">
              <div className={`flex items-center h-full ${isMobile ? 'justify-evenly' : 'justify-end gap-3'}`}>
                {user && (
                  <>
                    {/* AI Support Bot */}
                    <AImryAssistant />

                    {/* Notification Bell - Icon Only (no wrapper) */}
                    <NotificationBell />

                    {/* Dark Mode Toggle - Circular moon/sun button */}
                    <DarkModeToggle />

                    {/* User Menu - Icon + Text Only (no wrapper) */}
                    <UserMenu />
                  </>
                )}
              </div>
            </div>
          </header>

          {/* Main Content - Scrollable with padding for fixed header and bottom nav on mobile */}
          <main
            className="flex-1 overflow-y-auto page-background-crt"
            style={{
              marginTop: '5rem',
              paddingBottom: isMobile ? '5rem' : 0, // Space for bottom nav on mobile
            }}
          >
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Bottom Navigation - Mobile only */}
        {isMobile && <BottomNavVideogame />}
      </div>
    </>
  );
}
